import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Send, Save, CheckCircle, Copy } from "lucide-react";
import Button from "../../../components/ui/Button";
import InputText from "../../../components/ui/InputText";
import DatePicker from "../../../components/ui/DatePicker";
import UploadFile from "../../../components/ui/UploadFile";
import StarRating from "../../../components/ui/StarRating";
import Dialog from "../../../components/ui/Dialog";
import { Turnstile } from '@marsidev/react-turnstile';
import { toast, Toaster } from "react-hot-toast";
import Api from "../../../services/Api";

export default function FormSurat() {
    const [token, setToken] = useState("");
    const [kodeunique, setKodeunique] = useState("");
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting, isValid }
    } = useForm({ mode: "onChange" });

    const onSubmit = async (data) => {
        try {
            const formData = new FormData();
            formData.append("nosurat", data.nomor_surat);
            formData.append("asal_instansi", data.asal_instansi);
            formData.append("hp", data.nomor_hp);
            formData.append("perihal", data.perihal);
            // formData.append("tgl_kegiatan", data.tgl_kegiatan); // DatePicker usually returns a Date object or string, adjust if needed
            // If it's a date string, append it directly. If it's a Date object, .toISOString() or similar might be needed.
            // Assuming string for now based on standard HTML input or typical library behavior unless verified otherwise.
            formData.append("tgl_kegiatan", data.tgl_kegiatan);

            if (token) {
                formData.append("token", token);
            }

            if (data.rate) {
                formData.append("rate", data.rate);
            }

            if (data.file_surat && data.file_surat.length > 0) {
                // data.file_surat from react-hook-form with type="file" is usually a FileList
                formData.append("file_surat", data.file_surat[0]);
            }

            const res = await Api.post("/surat-masuk", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            setKodeunique(res.data.data.kode_surat);
            // console.log(res.data);
            toast.success("Surat berhasil dikirim!");
            setShowSuccessDialog(true);
            reset();
            setToken("");
        } catch (error) {
            console.error("Error submitting surat:", error);
            toast.error("Gagal mengirim surat. Silakan coba lagi.");
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 md:p-8">
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Documail - SMK Telkom Makassar</h2>
                <p className="text-gray-500 mt-1">Silahkan input surat .</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Nomor Surat */}
                    <InputText
                        id="nomor_surat"
                        label="Nomor Surat"
                        placeholder="Masukkan nomor surat"
                        error={errors.nomor_surat}
                        {...register("nomor_surat", {
                            required: "Nomor surat wajib diisi"
                        })}
                    />

                    {/* Asal Instansi */}
                    <InputText
                        id="asal_instansi"
                        label="Asal Instansi"
                        placeholder="Nama instansi pengirim"
                        error={errors.asal_instansi}
                        {...register("asal_instansi", {
                            required: "Asal instansi wajib diisi"
                        })}
                    />
                    <InputText
                        id="perihal"
                        label="Perihal"
                        placeholder="Perihal surat"
                        error={errors.perihal}
                        {...register("perihal", {
                            required: "Perihal wajib diisi"
                        })}
                    />

                    {/* Nomor HP */}
                    <InputText
                        id="nomor_hp"
                        label="Nomor HP"
                        type="tel"
                        placeholder="08xxxxxxxxxx"
                        error={errors.nomor_hp}
                        {...register("nomor_hp", {
                            required: "Nomor HP wajib diisi",
                            pattern: {
                                value: /^[0-9]+$/,
                                message: "Nomor HP hanya boleh angka"
                            },
                            minLength: {
                                value: 10,
                                message: "Minimal 10 digit"
                            }
                        })}
                    />

                    {/* Tanggal Kegiatan */}
                    <DatePicker
                        id="tgl_kegiatan"
                        label="Tanggal Kegiatan"
                        error={errors.tgl_kegiatan}
                        {...register("tgl_kegiatan", {
                            required: "Tanggal kegiatan wajib diisi"
                        })}
                    />


                </div>

                {/* File Upload Section - Full Width */}
                <div className="pt-2">
                    <UploadFile
                        id="file_surat"
                        label="Upload Dokumen Surat"
                        accept=".pdf,.jpg,.jpeg,.png"
                        error={errors.file_surat}
                        {...register("file_surat", {
                            required: "Dokumen surat wajib diupload"
                        })}
                    />
                </div>

                {/* Rating - Moved here */}
                <div className="pt-2">
                    <Controller
                        name="rate"
                        control={control}
                        rules={{ required: "Mohon berikan rating aplikasi ini" }}
                        render={({ field: { onChange, value } }) => (
                            <StarRating
                                label="Bagaimana performa aplikasi ini?"
                                value={value}
                                onChange={onChange}
                                error={errors.rate}
                            />
                        )}
                    />
                </div>
                {isValid && (
                    <Turnstile
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                        onSuccess={(token) => setToken(token)}
                    />
                )}

                <div className="pt-6 border-t border-gray-100 flex items-center justify-end gap-4">
                    <Button
                        variant="outline"
                        onClick={() => window.history.back()}
                    >
                        Batal
                    </Button>
                    <Button
                        type="submit"
                        isLoading={isSubmitting}
                        disabled={isSubmitting || !token}
                    >
                        <Save className="w-4 h-4 mr-2" />
                        Kirim Surat
                    </Button>
                </div>
            </form>

            <Dialog
                isOpen={showSuccessDialog}
                onClose={() => setShowSuccessDialog(false)}
                title="Berhasil"
                hideCloseButton={true}
            >
                <div className="flex flex-col items-center justify-center text-center space-y-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-2">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Surat Berhasil Dikirim!</h3>
                    <p className="text-gray-500">
                        Terima kasih, data surat Anda telah kami terima dan akan segera kami proses.
                        untuk mengecek status surat dengan masukan kode surat di bawah ini di menu tacking
                    </p>
                    <div className="flex items-center justify-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <span className="text-gray-600">Kode Surat:</span>
                        <code className="font-bold text-lg text-primary">{kodeunique}</code>
                        <button
                            type="button"
                            onClick={() => {
                                navigator.clipboard.writeText(kodeunique);
                                toast.success("Kode berhasil disalin!");
                            }}
                            className="p-1 hover:bg-gray-200 rounded-md transition-colors text-gray-500"
                            title="Salin Kode"
                        >
                            <Copy className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="w-full pt-2">
                        <Button onClick={() => setShowSuccessDialog(false)} className="w-full justify-center">
                            Tutup
                        </Button>
                    </div>
                </div>
            </Dialog>
            <Toaster />
        </div>
    );
}